import React, { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small (i) button that opens sources in a dialog: numbered citations with
 * links, then any one-line notes. Citations live here rather than on screen, so
 * a result reads as a number with its (i) beside it.
 *
 * Closes on Escape, on the backdrop or on ×, and hands focus back to the button.
 * Clicks stop at the button and the dialog: the dialog is portalled, but React
 * still bubbles its events to the button's ancestors (a clickable table row,
 * say), which must not react to them.
 *
 * Props:
 *   title       – dialog heading and the button's accessible name (default "Sources")
 *   description – optional line above the citations
 *   sources     – [{ cite, url?, used_for? }]
 *   notes       – [string]
 */
export default function SourcesButton({ title = 'Sources', description = '', sources = [], notes = [] }) {
  const [shown, setShown] = useState(false);
  const buttonRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!shown) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setShown(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown]);

  const close = (e) => { e?.stopPropagation(); setShown(false); buttonRef.current?.focus(); };
  const cited = sources.filter(src => src && src.cite);
  const lines = notes.filter(Boolean);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setShown(true); }}
        aria-label={title}
        title={title}
        style={{
          width: '16px', height: '16px', flexShrink: 0, padding: 0, borderRadius: '50%',
          border: '1px solid #b8c7d9', background: 'transparent', color: '#5a7fa8',
          fontSize: '10px', fontWeight: 700, fontFamily: 'Georgia, serif', lineHeight: '14px',
          cursor: 'pointer', verticalAlign: 'middle',
        }}
      >
        i
      </button>
      {shown && createPortal(
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(26, 26, 46, 0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
            textAlign: 'left',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '520px', maxHeight: '80vh',
              overflowY: 'auto', padding: '14px 18px 16px', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
              color: '#333', fontWeight: 400, fontStyle: 'normal',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h3 id={titleId} style={{ margin: 0, fontSize: '14px', color: '#1a56a0', flex: 1 }}>{title}</h3>
              <button
                ref={closeRef}
                type="button"
                onClick={close}
                aria-label="Close"
                style={{ border: 'none', background: 'none', fontSize: '20px', lineHeight: 1, color: '#5a6b7d', cursor: 'pointer', padding: '2px 6px' }}
              >
                ×
              </button>
            </div>
            {description && (
              <p style={{ margin: '8px 0 0', fontSize: '13px', lineHeight: 1.45 }}>{description}</p>
            )}
            {cited.length > 0 && (
              <ol style={{ margin: '10px 0 0', paddingLeft: '20px', fontSize: '13px', lineHeight: 1.45 }}>
                {cited.map((src, i) => (
                  <li key={i} style={{ marginBottom: '8px' }}>
                    <div>{src.cite}</div>
                    {src.used_for && <div style={{ fontSize: '12px', color: '#6b7a8a' }}>{src.used_for}</div>}
                    {/^https?:\/\//i.test(src.url || '') && (
                      <a href={src.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#1a73e8', wordBreak: 'break-all' }}>
                        {src.url.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            )}
            {lines.length > 0 && (
              <ul style={{
                margin: cited.length > 0 ? '4px 0 0' : '10px 0 0', paddingLeft: '18px',
                fontSize: '12px', color: '#555', lineHeight: 1.45,
                ...(cited.length > 0 ? { borderTop: '1px solid #eef1f4', paddingTop: '8px' } : {}),
              }}>
                {lines.map((note, i) => <li key={i} style={{ marginBottom: '3px' }}>{note}</li>)}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
